import { AuditTab } from '@/components/ui/audit-tab';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	ChevronLeft,
	ChevronRight,
	Activity,
	FileText,
	History,
	Receipt
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ParkingSessionLedger } from './parking-session-ledger';
import { ParkingSessionDetails } from './parking-session-details';
import { ParkingSessionJourneyLogs } from './parking-session-journey-logs';
import { ParkingSessionServices } from './parking-session-services';
import type { ParkingSessionDetail } from './types';

interface ParkingSessionDetailsTabsProps {
	session: ParkingSessionDetail;
	activeTab: string;
	onTabChange: (value: string) => void;
}

export const ParkingSessionDetailsTabs = ({
	session,
	activeTab,
	onTabChange
}: ParkingSessionDetailsTabsProps) => {
	const { t } = useTranslation('parkingSessions');
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const scrollContainerRef = useRef<HTMLDivElement>(null);

	const checkScrollButtons = () => {
		const container = scrollContainerRef.current;
		if (!container) return;

		setCanScrollLeft(container.scrollLeft > 0);
		setCanScrollRight(
			container.scrollLeft < container.scrollWidth - container.clientWidth
		);
	};

	useEffect(() => {
		checkScrollButtons();
		const container = scrollContainerRef.current;
		if (container) {
			container.addEventListener('scroll', checkScrollButtons);
			return () =>
				container.removeEventListener('scroll', checkScrollButtons);
		}
	}, []);

	const scrollLeft = () => {
		scrollContainerRef.current?.scrollBy({
			left: -200,
			behavior: 'smooth'
		});
	};

	const scrollRight = () => {
		scrollContainerRef.current?.scrollBy({ left: 200, behavior: 'smooth' });
	};

	return (
		<Tabs
			value={activeTab}
			onValueChange={onTabChange}
			className="flex flex-col h-full min-h-0"
		>
			{/* Tab Bar - fixed height at top */}
			<div className="flex-shrink-0 pb-2">
				<div className="flex items-center gap-2">
					{/* Left scroll button */}
					{canScrollLeft && (
						<Button
							variant="ghost"
							size="sm"
							onClick={scrollLeft}
							className="flex-shrink-0 w-8 h-8 p-0 rounded-md hover:bg-accent"
						>
							<ChevronLeft className="w-4 h-4" />
						</Button>
					)}
					{/* Tab List Container */}
					<div
						ref={scrollContainerRef}
						className="flex-1 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]"
					>
						<TabsList className="mx-0 w-max min-w-full gap-2 bg-transparent p-0">
							<TabsTrigger
								value="details"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<FileText className="w-4 h-4" />
								<span>{t('tabs.details')}</span>
							</TabsTrigger>
							<TabsTrigger
								value="ledger"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<Receipt className="w-4 h-4" />
								<span>{t('tabs.ledger')}</span>
							</TabsTrigger>
							<TabsTrigger
								value="events"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<Activity className="w-4 h-4" />
								<span>{t('tabs.events')}</span>
							</TabsTrigger>
							<TabsTrigger
								value="audit"
								align="right"
								className="flex items-center gap-2 px-4 py-2 font-normal data-[state=active]:bg-background data-[state=active]:shadow-sm data-[state=active]:font-semibold"
							>
								<History className="w-4 h-4" />
								<span>{t('tabs.audit')}</span>
							</TabsTrigger>
						</TabsList>
					</div>
					{/* Right scroll button */}
					{canScrollRight && (
						<Button
							variant="ghost"
							size="sm"
							onClick={scrollRight}
							className="flex-shrink-0 w-8 h-8 p-0 rounded-md hover:bg-accent"
						>
							<ChevronRight className="w-4 h-4" />
						</Button>
					)}
				</div>
			</div>

			{/* Tab Content - scrollable area with min-height for stable modal sizing */}
			<div className="flex-1 min-h-[350px] overflow-y-auto overflow-x-hidden">
				<TabsContent value="details" className="mt-0">
					<ParkingSessionDetails session={session} />
				</TabsContent>
				<TabsContent value="events" className="mt-0">
					<ParkingSessionJourneyLogs
						sessionId={session.parkingSessionId}
						isActive={activeTab === 'events'}
					/>
				</TabsContent>
				<TabsContent value="ledger" className="mt-0">
					<ParkingSessionLedger
						sessionId={session.parkingSessionId}
						isActive={activeTab === 'ledger'}
					/>
				</TabsContent>
				<TabsContent value="services" className="mt-0">
					<ParkingSessionServices
						sessionId={session.parkingSessionId}
					/>
				</TabsContent>
				<TabsContent value="audit" className="mt-0">
					<AuditTab
						data={{
							createdAt: session.entryDateTime,
							updatedAt: session.exitDateTime
						}}
					/>
				</TabsContent>
			</div>
		</Tabs>
	);
};
